import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Lock, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Site Access gate. When the platform is restricted (private_beta/maintenance) and the caller is not
   an authorised (allowlisted) account, render a minimal "Sign in to continue" screen instead of the
   app — no marketing, no catalogue, no signup. PUBLIC mode renders children unchanged. */
export default function SiteAccessGate({ children }) {
  const { token, user, login, verifyMfa, register } = useAuth();
  const [state, setState] = useState({ loading: true, restricted: false, authorised: false });

  const check = useCallback(async () => {
    try {
      const t = localStorage.getItem('token');
      const headers = t ? { Authorization: `Bearer ${t}` } : {};
      const { data } = await axios.get(`${API_URL}/api/site-access`, { headers });
      setState({ loading: false, restricted: !!data.restricted, authorised: !!data.authorised });
    } catch {
      // Fail open on a status-check error so we never hard-lock the app on a transient blip.
      setState({ loading: false, restricted: false, authorised: false });
    }
  }, []);

  useEffect(() => { check(); }, [check, token, user]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950" data-testid="site-access-loading">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }
  if (!state.restricted || state.authorised) return children;
  return <SignInGate onSignedIn={check} login={login} verifyMfa={verifyMfa} register={register} />;
}

function SignInGate({ onSignedIn, login, verifyMfa, register }) {
  const [mode, setMode] = useState('signin'); // signin | register
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [mfa, setMfa] = useState(null); // { pendingToken }
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);

  // Prefill invite code from a shared ?invite= link and jump straight to sign-up.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inv = params.get('invite');
    if (inv) { setInviteCode(inv); setMode('register'); }
  }, []);

  const finish = () => window.location.reload();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const res = await login(email.trim(), password);
      if (res?.mfaRequired) { setMfa({ pendingToken: res.mfaPendingToken }); setBusy(false); return; }
      if (res?.mfaSetupRequired) { setError('This account must complete MFA setup before access.'); setBusy(false); return; }
      finish();
    } catch (err) {
      setError(err.response?.data?.detail || 'Sign in failed. Check your details and try again.');
      setBusy(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await register({ email: email.trim(), password, invite_code: inviteCode.trim() });
      finish();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Sign up failed. Check your invite code and details.');
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await verifyMfa(mfa.pendingToken, code.trim(), recovery);
      finish();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Try again.');
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-slate-500 focus:outline-none";

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4" data-testid="site-access-gate">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-12 h-12 rounded-2xl bg-slate-800 flex items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-slate-300" />
          </div>
          <h1 className="text-xl font-semibold text-white" data-testid="site-access-gate-title">
            {mfa ? 'Two-factor authentication' : (mode === 'register' ? 'Create your beta account' : 'Sign in to continue')}
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            {mfa
              ? (recovery ? 'Enter one of your saved recovery codes.' : 'Enter the 6-digit code from your authenticator app.')
              : (mode === 'register'
                ? 'This platform is in private beta. Enter your invite code to create your account.'
                : 'This platform is currently private. Please sign in with an authorised account.')}
          </p>
        </div>
        {error && <div className="mb-4 rounded-lg bg-red-950/50 border border-red-900/60 px-3 py-2 text-sm text-red-300" data-testid="site-access-error">{error}</div>}
        {mfa ? (
          <form onSubmit={submitMfa} className="space-y-3" data-testid="site-access-mfa-form">
            <input inputMode={recovery ? 'text' : 'numeric'} required value={code} onChange={(e) => setCode(e.target.value)}
              placeholder={recovery ? 'Recovery code' : 'Authentication code'}
              data-testid="site-access-mfa-code" className={inputCls} />
            <button type="submit" disabled={busy} data-testid="site-access-mfa-submit"
              className="w-full rounded-lg bg-white text-slate-900 font-semibold text-sm py-2.5 hover:bg-slate-200 disabled:opacity-60 transition-colors">
              {busy ? 'Verifying…' : 'Verify'}
            </button>
            <button type="button" onClick={() => { setRecovery((r) => !r); setError(''); setCode(''); }}
              className="w-full text-center text-sm text-slate-400 hover:text-slate-200 transition-colors pt-1" data-testid="site-access-mfa-toggle-recovery">
              {recovery ? 'Use your authenticator app instead' : 'Use a recovery code instead'}
            </button>
          </form>
        ) : mode === 'register' ? (
          <form onSubmit={submitRegister} className="space-y-3" data-testid="site-access-register-form">
            <input type="text" required value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder="Invite code"
              data-testid="site-access-invite-code" className={inputCls} />
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              data-testid="site-access-register-email" className={inputCls} />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Choose a password (min 12 chars)"
              data-testid="site-access-register-password" className={inputCls} />
            <button type="submit" disabled={busy} data-testid="site-access-register-submit"
              className="w-full rounded-lg bg-white text-slate-900 font-semibold text-sm py-2.5 hover:bg-slate-200 disabled:opacity-60 transition-colors">
              {busy ? 'Creating account…' : 'Create account'}
            </button>
            <button type="button" onClick={() => { setMode('signin'); setError(''); }}
              className="w-full text-center text-sm text-slate-400 hover:text-slate-200 transition-colors pt-1" data-testid="site-access-to-signin">
              Already have an account? Sign in
            </button>
          </form>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="site-access-signin-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              data-testid="site-access-email" className={inputCls} />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
              data-testid="site-access-password" className={inputCls} />
            <button type="submit" disabled={busy} data-testid="site-access-submit"
              className="w-full rounded-lg bg-white text-slate-900 font-semibold text-sm py-2.5 hover:bg-slate-200 disabled:opacity-60 transition-colors">
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <button type="button" onClick={() => { setMode('register'); setError(''); }}
              className="w-full text-center text-sm text-slate-400 hover:text-slate-200 transition-colors pt-1" data-testid="site-access-to-register">
              Have an invite code? Create your account
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
